// src/components/Support/pptExports/pptExportHelpers.js

export function getOrderedCategories(categoryMap = {}, categorizedVMs = {}) {
    return Object.keys(categorizedVMs || {}).sort((a, b) => {
        const aOrder = categoryMap?.[a]?.order ?? categoryMap?.[a]?.sortOrder ?? 999
        const bOrder = categoryMap?.[b]?.order ?? categoryMap?.[b]?.sortOrder ?? 999

        if (aOrder !== bOrder) return aOrder - bOrder

        const aName = categoryMap?.[a]?.name || ''
        const bName = categoryMap?.[b]?.name || ''
        return aName.localeCompare(bName)
    })
}

/**
 * Returns Value Messages in their persisted display order.
 * This keeps PPT exports consistent with the website after
 * users manually reorder Value Messages.
 */
export function getOrderedValueMessages(valueMessages = []) {
    if (!Array.isArray(valueMessages)) {
        return []
    }

    return [...valueMessages].sort((a, b) => {
        const aOrder = Number.isFinite(Number(a?.order)) ? Number(a.order) : Number.MAX_SAFE_INTEGER

        const bOrder = Number.isFinite(Number(b?.order)) ? Number(b.order) : Number.MAX_SAFE_INTEGER

        if (aOrder !== bOrder) {
            return aOrder - bOrder
        }

        const aName = a?.name || ''
        const bName = b?.name || ''

        return aName.localeCompare(bName)
    })
}

export function getMessageStatus(vm) {
    return vm?.status || 'N/A'
}

export function getMessagePriority(vm) {
    return vm?.priority || 'N/A'
}

export function getEvidenceTypeLabel(ev) {
    if (ev?.internal === true) return 'Internal'
    if (ev?.internal === false) return 'External'
    return 'N/A'
}

export function getCategoryName(categoryMap = {}, categoryId) {
    return categoryMap?.[categoryId]?.name || 'Uncategorized'
}

export function getActivityDisplayLabel(activity) {
    const title = activity?.title || '(Untitled Activity)'

    const status = activity?.status || activity?.activityStatus || activity?.studyStatus || 'Uncategorized'

    return `${title} (${status})`
}

export function getActivityTitle(activity) {
    return activity?.title || '(Untitled Activity)'
}

export function getActivityId(activity) {
    return activity?._id || activity?.id || activity?.activityId || activity?.studyId || activity?.title || ''
}

export function buildActivityNumberingMap(rows = []) {
    const activityNumberMap = new Map()
    let counter = 1

    for (const row of rows) {
        for (const activity of row?.acts || []) {
            const activityId = getActivityId(activity)
            if (!activityId) continue

            if (!activityNumberMap.has(activityId)) {
                activityNumberMap.set(activityId, counter)
                counter += 1
            }
        }
    }

    return activityNumberMap
}

export function getActivityNumber(activity, activityNumberMap) {
    const activityId = getActivityId(activity)
    return activityNumberMap?.get(activityId) || ''
}

export function toSuperscriptNumber(value) {
    const map = {
        0: '⁰',
        1: '¹',
        2: '²',
        3: '³',
        4: '⁴',
        5: '⁵',
        6: '⁶',
        7: '⁷',
        8: '⁸',
        9: '⁹',
    }

    return String(value)
        .split('')
        .map((char) => map[char] || char)
        .join('')
}

export function appendAtlasBrandingToNotes(slide) {
    const branding = "Generated with Arysana's ATLAS"

    try {
        // Access existing notes (internal field)
        const existingNotes = slide._notes || ''

        const spacer = existingNotes ? '\n\n\n' : ''

        slide.addNotes(`${existingNotes}${spacer}${branding}`)
    } catch (err) {
        console.warn('⚠️ Unable to append ATLAS branding to notes', err)
        slide.addNotes(branding)
    }
}

export function buildActivityNotesText(activityNumberMap, rows = [], opts = {}) {
    const { getStatusDisplay = null } = opts
    const activityByNumber = new Map()

    function formatDate(value) {
        if (!value) return ''
        const d = new Date(value)
        if (Number.isNaN(d.getTime())) return ''
        return d.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric',
        })
    }

    /// Updating the notes section to include information about the activity dates being planned or actual
    for (const row of rows) {
        for (const activity of row?.acts || []) {
            const number = getActivityNumber(activity, activityNumberMap)
            if (!number || activityByNumber.has(number)) continue

            const title = activity?.title || '(Untitled Activity)'
            const rawStatus = activity?.status || activity?.activityStatus || activity?.studyStatus || 'Uncategorized'

            // Render the org's label for the status id ('Initiated' -> 'Active' for Otsuka).
            const status = typeof getStatusDisplay === 'function' ? getStatusDisplay(rawStatus) : rawStatus

            const timelineDates = resolveActivityTimelineDates(activity)

            const start = formatDate(timelineDates.resolvedStartDate)
            const end = formatDate(timelineDates.resolvedEndDate)

            const startLabel = timelineDates.startDateType === 'planned' && start ? `${start} (Planned)` : start

            const endLabel = timelineDates.endDateType === 'planned' && end ? `${end} (Planned)` : end

            const timeline = start || end ? ` | Timeline: ${startLabel || 'Unknown'} – ${endLabel || 'Unknown'}` : ''

            activityByNumber.set(number, `${title} | Status: ${status}${timeline}`)
        }
    }

    return Array.from(activityByNumber.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([number, label]) => `${number}. ${label}`)
        .join('\n')
}

/**
 * Resolves the Activity dates that should be displayed in PPT exports.
 *
 * Date precedence:
 * - Use startDate when available; otherwise use plannedStartDate.
 * - Use endDate when available; otherwise use plannedEndDate.
 */
export function resolveActivityTimelineDates(activity = {}) {
    const actualStartDate = activity?.startDate || null
    const actualEndDate = activity?.endDate || null

    const plannedStartDate = activity?.plannedStartDate || null
    const plannedEndDate = activity?.plannedEndDate || null

    const resolvedStartDate = actualStartDate || plannedStartDate
    const resolvedEndDate = actualEndDate || plannedEndDate

    const startDateType = actualStartDate ? 'actual' : plannedStartDate ? 'planned' : 'missing'

    const endDateType = actualEndDate ? 'actual' : plannedEndDate ? 'planned' : 'missing'

    return {
        actualStartDate,
        actualEndDate,
        plannedStartDate,
        plannedEndDate,

        resolvedStartDate,
        resolvedEndDate,

        startDateType,
        endDateType,

        hasPlannedStartDate: startDateType === 'planned',
        hasPlannedEndDate: endDateType === 'planned',
        hasPlannedDate: startDateType === 'planned' || endDateType === 'planned',

        hasActualStartDate: startDateType === 'actual',
        hasActualEndDate: endDateType === 'actual',
        hasActualDate: startDateType === 'actual' || endDateType === 'actual',

        hasAnyTimelineDate: Boolean(resolvedStartDate || resolvedEndDate),

        hasCompleteTimeline: Boolean(resolvedStartDate && resolvedEndDate),
    }
}
